import { formatAgo, formatWithUnit } from '../lib/format'
import type { MetricInfo, Sample } from '../lib/types'
import { StatusBadge } from './Status'

/** Eine Zeile der Karte: eine Kennzahl oder eine Zeile einer Tabelle. */
export interface ZustandsZeile {
  key: string
  name: string
  text: string
  /** Bewertung laut Template; ohne Zustände und Schwellen keine. */
  stufe?: 'ok' | 'info' | 'warn' | 'crit'
  time: string
}

// Die Stufe steht immer auch als Wort da, nie nur als Farbe (docs/design.md).
const STUFENTEXT = { ok: 'In Ordnung', info: 'Hinweis', warn: 'Warnung', crit: 'Kritisch' } as const

/** Wie eine Tabellenzeile heißt: die erste Beschriftung, die kein Index ist. */
function zeilenName(labels: Record<string, string> | undefined): string {
  if (!labels) return ''
  const keys = Object.keys(labels)
    .filter((k) => k !== 'index' && labels[k])
    .sort()
  const erste = keys[0]
  return erste !== undefined ? (labels[erste] ?? '') : (labels.index ?? '')
}

/** Ein Wert und seine Bewertung nach dem Template. */
function bewerte(info: MetricInfo, value: number): Pick<ZustandsZeile, 'text' | 'stufe'> {
  if (info.states && info.states.length > 0) {
    const st = info.states.find((s) => s.value === value)
    // Ein Code, den das Template nicht kennt, erscheint als Zahl – ohne
    // Bewertung, die niemand festgelegt hat.
    if (!st) return { text: `Code ${value}` }
    return { text: st.label, stufe: st.level }
  }
  const text = formatWithUnit(value, info.unit)
  const t = info.thresholds
  if (!t || (t.warn === undefined && t.crit === undefined)) return { text }
  const below = t.direction === 'below'
  const reached = (limit?: number) =>
    limit !== undefined && (below ? value <= limit : value >= limit)
  if (reached(t.crit)) return { text, stufe: 'crit' }
  if (reached(t.warn)) return { text, stufe: 'warn' }
  return { text, stufe: 'ok' }
}

/**
 * Die Zeilen der Karte „Zustand": jede Kennzahl, die ihr Template beim Namen
 * nennt, in der Reihenfolge des Templates. Was schon woanders steht (Kacheln,
 * Interfaces, Dateisysteme), bleibt außen vor.
 */
export function zustandsZeilen(
  infos: MetricInfo[],
  latest: Sample[],
  schonGezeigt: Set<string>,
): ZustandsZeile[] {
  const out: ZustandsZeile[] = []
  for (const info of infos) {
    if (!info.label || schonGezeigt.has(info.key)) continue
    if (info.requires) {
      // Die HA-Werte einer FortiGate gelten nur im Cluster (Stufe 6.2).
      const bedingung = latest.find(
        (w) => w.metric === info.requires?.metric && (!w.labels || Object.keys(w.labels).length === 0),
      )
      if (!bedingung || !info.requires.in.includes(bedingung.value)) continue
    }
    const werte = latest.filter((s) => s.metric === info.key)
    if (info.table) {
      const zeilen = werte
        .filter((s) => s.labels && Object.keys(s.labels).length > 0)
        .map((s) => ({ s, name: zeilenName(s.labels) }))
        .sort((a, b) => a.name.localeCompare(b.name, 'de', { numeric: true }))
      for (const { s, name } of zeilen) {
        out.push({
          key: `${info.key}|${s.labels?.index ?? name}`,
          name: `${info.label} ${name}`,
          ...bewerte(info, s.value),
          time: s.time,
        })
      }
      continue
    }
    const s = werte.find((w) => !w.labels || Object.keys(w.labels).length === 0)
    if (!s) continue
    out.push({ key: info.key, name: info.label, ...bewerte(info, s.value), time: s.time })
  }
  return out
}

/**
 * Zustand und Messwerte, die ein Template über die Standardkacheln hinaus
 * erhebt: bei einer USV Ausgang, Batterie und Restlaufzeit, bei einem NAS die
 * Festplatten und Speicherpools, bei einem Drucker Toner und Papier.
 */
export function ZustandKarte({ zeilen }: { zeilen: ZustandsZeile[] }) {
  if (zeilen.length === 0) return null
  return (
    <section className="card" aria-labelledby="zustand-titel">
      <div className="card-head">
        <h2 className="card-title" id="zustand-titel">
          Zustand
        </h2>
        <span className="muted" style={{ fontSize: 12 }}>
          Bewertung laut Template
        </span>
      </div>
      <table className="data">
        <thead>
          <tr>
            <th>Messgröße</th>
            <th>Wert</th>
            <th>Bewertung</th>
            <th>Stand</th>
          </tr>
        </thead>
        <tbody>
          {zeilen.map((z) => (
            <tr key={z.key}>
              <td>{z.name}</td>
              <td className="mono">{z.text}</td>
              <td>
                {z.stufe ? (
                  <StatusBadge state={z.stufe} label={STUFENTEXT[z.stufe]} />
                ) : (
                  <span className="muted">–</span>
                )}
              </td>
              <td className="muted">{formatAgo(z.time)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}
